import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, BarChart3, CreditCard, MessageSquare, Zap, DollarSign,
  Search, ChevronDown, ChevronUp, Trash2, Lock, Eye, EyeOff,
  ArrowLeft, FileText, Settings, PenSquare, Plus, Globe,
  ChevronLeft, ChevronRight
} from 'lucide-react';

import { API } from '@/config';

const PLAN_COLORS = {
  free: 'bg-zinc-100 text-zinc-700', single: 'bg-blue-100 text-blue-700',
  starter: 'bg-blue-100 text-blue-700', pro: 'bg-emerald-100 text-emerald-700',
  business: 'bg-amber-100 text-amber-700',
};

function Paginator({ page, pages, total, onPageChange, label = 'items' }) {
  if (pages <= 1) return total > 0 ? <p className="text-xs text-muted-foreground text-center mt-3">{total} {label}</p> : null;
  return (
    <div className="flex items-center justify-center gap-2 mt-4">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="h-7 text-xs gap-1">
        <ChevronLeft className="h-3 w-3" /> Prev
      </Button>
      <span className="text-xs text-muted-foreground px-2">
        Page {page} of {pages} ({total} {label})
      </span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="h-7 text-xs gap-1">
        Next <ChevronRight className="h-3 w-3" />
      </Button>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('whisper-admin-token'));
  const [isAuth, setIsAuth] = useState(!!localStorage.getItem('whisper-admin-token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [logging, setLogging] = useState(false);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState({ invoices: [], total: 0, total_revenue: 0 });
  const [widgets, setWidgets] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem('whisper-admin-token'));
  const [plans, setPlans] = useState({});
  // Blog
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', slug: '', excerpt: '', content: '', cover_image: '', tags: '', published: false, meta_title: '', meta_description: '' });
  // Demo chats
  const [demoChats, setDemoChats] = useState({ chats: [], total: 0, pages: 1 });
  const [expandedChat, setExpandedChat] = useState(null);
  // Renewals
  const [renewals, setRenewals] = useState({ renewals: [], total: 0, pages: 1, stats: {} });
  const [renewalsPage, setRenewalsPage] = useState(1);
  // Pagination
  const [usersPage, setUsersPage] = useState(1);
  const [subsPage, setSubsPage] = useState(1);
  const [subsFilter, setSubsFilter] = useState('');
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [widgetsPage, setWidgetsPage] = useState(1);
  const [blogPage, setBlogPage] = useState(1);
  const [demoChatPage, setDemoChatPage] = useState(1);
  const PAGE_SIZE = 10;

  const loadAll = useCallback(async (token) => {
    if (!token) return;
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [d, u, s, i, w, p, b, dc, rn] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, h),
        axios.get(`${API}/admin/users?limit=${PAGE_SIZE}&skip=${(usersPage-1)*PAGE_SIZE}&search=${userSearch}`, h),
        axios.get(`${API}/admin/subscriptions?page=${subsPage}&limit=${PAGE_SIZE}${subsFilter ? '&status=' + subsFilter : ''}`, h),
        axios.get(`${API}/admin/invoices?page=${invoicesPage}&limit=${PAGE_SIZE}`, h),
        axios.get(`${API}/admin/widgets?page=${widgetsPage}&limit=${PAGE_SIZE}`, h),
        axios.get(`${API}/plans`),
        axios.get(`${API}/admin/blog?page=${blogPage}&limit=${PAGE_SIZE}`, h),
        axios.get(`${API}/admin/demo-chats?page=${demoChatPage}&limit=${PAGE_SIZE}`, h),
        axios.get(`${API}/admin/renewals?page=${renewalsPage}&limit=${PAGE_SIZE}`, h),
      ]);
      setDashboard(d.data);
      setUsers(u.data.users);
      setUsersTotal(u.data.total);
      setSubscriptions(s.data);
      setInvoices(i.data);
      setWidgets(w.data);
      setPlans(p.data);
      setBlogPosts(b.data);
      setDemoChats(dc.data);
      setRenewals(rn.data);
      setIsAuth(true);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setIsAuth(false);
        setAdminToken(null);
        localStorage.removeItem('whisper-admin-token');
      }
    } finally {
      setLoading(false);
    }
  }, [userSearch]);

  useEffect(() => {
    if (adminToken) { loadAll(adminToken); }
  }, [adminToken, loadAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      const token = res.data.token;
      localStorage.setItem('whisper-admin-token', token);
      setAdminToken(token);
      setLoading(true);
      toast.success('Admin access granted');
    } catch { toast.error('Invalid admin credentials'); }
    finally { setLogging(false); }
  };

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${adminToken}` } });

  const updateUserPlan = async (userId, plan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { plan }, getAuthHeaders());
      toast.success(`Plan updated to ${plan}`);
      loadAll(adminToken);
    } catch { toast.error('Failed to update'); }
  };

  const toggleUserDisabled = async (userId, disabled) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_disabled: disabled }, getAuthHeaders());
      toast.success(disabled ? 'User disabled' : 'User enabled');
      loadAll(adminToken);
    } catch { toast.error('Failed to update'); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This removes all their widgets, data, and cancels subscriptions. Cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeaders());
      toast.success('User deleted');
      loadAll(adminToken);
    } catch { toast.error('Failed to delete'); }
  };

  const loadUserDetail = async (userId) => {
    if (expandedUser === userId) { setExpandedUser(null); return; }
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, getAuthHeaders());
      setUserDetail(res.data);
      setExpandedUser(userId);
    } catch { toast.error('Failed to load user details'); }
  };

  const handleLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('whisper-admin-token');
    setIsAuth(false);
  };

  // ─── Login Screen ───
  if (!isAuth && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-[#18181B] flex items-center justify-center">
              <Lock className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold text-foreground">Admin Panel</span>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <div className="space-y-1.5">
              <Label>Admin Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@whisperai.one" data-testid="admin-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="admin-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={logging} data-testid="admin-login-btn">
              {logging ? 'Authenticating...' : 'Access Admin Panel'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading admin...</div>;

  // ─── Admin Dashboard ───
  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      <header className="border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-[#18181B] flex items-center justify-center">
            <Lock className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-bold text-foreground">Whisper.AI Admin</span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">Logout</Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-8" data-testid="admin-stats">
            {[
              { label: 'Users', value: dashboard.total_users, icon: Users },
              { label: 'Unverified', value: dashboard.unverified_users || 0, icon: Lock },
              { label: 'Widgets', value: dashboard.total_widgets, icon: MessageSquare },
              { label: 'Active Subs', value: dashboard.active_subscriptions, icon: CreditCard },
              { label: 'Msgs/Month', value: dashboard.total_messages_this_month?.toLocaleString(), icon: Zap },
              { label: 'Revenue/Mo', value: `$${dashboard.month_revenue}`, icon: DollarSign, highlight: true },
              { label: 'Total Revenue', value: `$${dashboard.total_revenue}`, icon: BarChart3, highlight: true },
              { label: 'Invoices', value: dashboard.total_invoices, icon: FileText },
            ].map((s) => (
              <div key={s.label} className={`p-4 rounded-xl border border-border ${s.highlight ? 'bg-[#10B981]/5 border-[#10B981]/20' : 'bg-card'}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                  <s.icon className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-medium uppercase tracking-wider">{s.label}</span>
                </div>
                <p className={`text-lg font-bold ${s.highlight ? 'text-[#10B981]' : 'text-foreground'}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Plan Distribution */}
        {dashboard?.plan_distribution && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="plan-distribution">
            {Object.entries(dashboard.plan_distribution).map(([plan, count]) => (
              <div key={plan} className={`px-3 py-1.5 rounded-full text-xs font-medium ${PLAN_COLORS[plan] || 'bg-zinc-100 text-zinc-700'}`}>
                {plans[plan]?.name || plan}: {count} users
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="users" data-testid="admin-tabs">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="users">Users ({usersTotal})</TabsTrigger>
            <TabsTrigger value="subscriptions">Subs ({subscriptions.total || 0})</TabsTrigger>
            <TabsTrigger value="renewals">Renewals</TabsTrigger>
            <TabsTrigger value="invoices">Invoices ({invoices.total || 0})</TabsTrigger>
            <TabsTrigger value="widgets">Widgets ({widgets.total || 0})</TabsTrigger>
            <TabsTrigger value="blog">Blog ({blogPosts.total || 0})</TabsTrigger>
            <TabsTrigger value="demo-chats">Demo ({demoChats.total || 0})</TabsTrigger>
          </TabsList>

          {/* ─── Users Tab ─── */}
          <TabsContent value="users">
            <div className="mb-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadAll(adminToken)}
                  placeholder="Search by name or email..."
                  className="pl-9"
                  data-testid="admin-user-search"
                />
              </div>
            </div>
            <div className="space-y-2" data-testid="admin-users-list">
              {users.map((u) => (
                <div key={u.id} className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => loadUserDetail(u.id)}>
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{u.name} {u.is_disabled && <span className="text-destructive text-xs">(disabled)</span>}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground">{u.widget_count} bots · {u.messages_this_month} msgs</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[u.plan] || PLAN_COLORS.free}`}>{u.plan}</span>
                      {expandedUser === u.id ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </div>

                  {expandedUser === u.id && userDetail && (
                    <div className="border-t border-border p-4 bg-muted/20 space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <div><span className="text-muted-foreground">User ID</span><p className="font-mono text-foreground mt-0.5">{u.id}</p></div>
                        <div><span className="text-muted-foreground">Joined</span><p className="text-foreground mt-0.5">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</p></div>
                        <div><span className="text-muted-foreground">Widgets</span><p className="text-foreground mt-0.5">{userDetail.widgets?.length || 0}</p></div>
                        <div><span className="text-muted-foreground">Invoices</span><p className="text-foreground mt-0.5">{userDetail.invoices?.length || 0}</p></div>
                      </div>

                      {/* Subscription */}
                      {userDetail.subscription && (
                        <div className="p-2 rounded-lg bg-card border border-border text-xs">
                          <span className="text-muted-foreground">Active sub:</span> <span className="font-mono text-foreground">{userDetail.subscription.subscription_id}</span>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        <select
                          className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground"
                          value={u.plan}
                          onChange={(e) => updateUserPlan(u.id, e.target.value)}
                          data-testid={`admin-change-plan-${u.id}`}
                        >
                          {Object.entries(plans).map(([k, v]) => (
                            <option key={k} value={k}>{v.name} (${v.price}/mo)</option>
                          ))}
                        </select>
                        <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => toggleUserDisabled(u.id, !u.is_disabled)}>
                          {u.is_disabled ? 'Enable' : 'Disable'}
                        </Button>
                        <Button size="sm" variant="destructive" className="text-xs h-8" onClick={() => deleteUser(u.id, u.email)}>
                          <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Paginator page={usersPage} pages={Math.ceil(usersTotal / PAGE_SIZE)} total={usersTotal} label="users" onPageChange={(p) => { setUsersPage(p); loadAll(adminToken); }} />
          </TabsContent>

          {/* ─── Subscriptions Tab ─── */}
          <TabsContent value="subscriptions">
            {/* Status counts */}
            {subscriptions.counts && (
              <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="subs-filters">
                {[
                  { key: '', label: 'All', count: subscriptions.counts.all },
                  { key: 'active', label: 'Active', count: subscriptions.counts.active, color: 'bg-emerald-100 text-emerald-700' },
                  { key: 'cancelled', label: 'Cancelled', count: subscriptions.counts.cancelled, color: 'bg-red-100 text-red-700' },
                  { key: 'created', label: 'Pending', count: subscriptions.counts.created, color: 'bg-amber-100 text-amber-700' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => { setSubsFilter(f.key); setSubsPage(1); setTimeout(() => loadAll(adminToken), 50); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      subsFilter === f.key ? 'bg-[#10B981] text-white' : f.color || 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-2" data-testid="admin-subs-list">
              {(!subscriptions.subscriptions || subscriptions.subscriptions.length === 0) ? (
                <p className="text-center text-sm text-muted-foreground py-12">
                  {subsFilter ? `No ${subsFilter} subscriptions` : 'No subscriptions yet'}
                </p>
              ) : subscriptions.subscriptions.map((s) => (
                <div key={s.subscription_id} className="p-4 rounded-xl border border-border bg-card flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground">{s.user_name || s.user_email}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PLAN_COLORS[s.user_plan] || PLAN_COLORS.free}`}>{s.user_plan}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{s.user_email}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Plan: {s.plan}
                      {s.activated_at && ` · Since ${new Date(s.activated_at).toLocaleDateString()}`}
                      {s.cancelled_at && ` · Cancelled ${new Date(s.cancelled_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      s.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      s.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>{s.status}</span>
                    <p className="text-[10px] text-muted-foreground mt-1 font-mono">{s.subscription_id}</p>
                  </div>
                </div>
              ))}
            </div>
            <Paginator page={subsPage} pages={subscriptions.pages || 1} total={subscriptions.total || 0} label="subscriptions" onPageChange={(p) => { setSubsPage(p); loadAll(adminToken); }} />
          </TabsContent>

          {/* ─── Renewals Tab ─── */}
          <TabsContent value="renewals">
            {renewals.stats && (
              <div className="flex gap-3 mb-4 items-center flex-wrap">
                <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Paid: {renewals.stats.paid || 0}</div>
                <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pending: {renewals.stats.pending || 0}</div>
                {renewals.stats.total_amount > 0 && (
                  <div className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#10B981]/10 text-[#10B981]">Total: ₹{(renewals.stats.total_amount / 100).toLocaleString()}</div>
                )}
                <Button size="sm" variant="outline" className="text-xs h-7 ml-auto" onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/admin/sync-renewals`, {}, getAuthHeaders());
                    toast.success(res.data.message || 'Synced');
                    loadAll(adminToken);
                  } catch { toast.error('Sync failed'); }
                }} data-testid="sync-renewals-btn">Sync from Razorpay</Button>
              </div>
            )}
            <div className="space-y-2" data-testid="admin-renewals-list">
              {(!renewals.renewals || renewals.renewals.length === 0) ? (
                <div className="text-center text-sm text-muted-foreground py-12">
                  <p>No renewals yet.</p>
                  <p className="text-xs mt-1">Renewal payments will appear here when subscriptions auto-renew monthly.</p>
                </div>
              ) : renewals.renewals.map((r) => (
                <div key={r.renewal_id} className="p-4 rounded-xl border border-border bg-card flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground">{r.user_name || r.user_email}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PLAN_COLORS[r.plan] || PLAN_COLORS.free}`}>{r.plan}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {r.user_email}
                      {r.method && ` · ${r.method}`}
                      {r.created_at && ` · ${new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                    {r.amount > 0 && <p className="text-sm font-bold text-foreground mt-1">₹{(r.amount / 100).toLocaleString()}</p>}
                    {r.payment_id && <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{r.payment_id}</p>}
                  </div>
                </div>
              ))}
            </div>
            <Paginator page={renewalsPage} pages={renewals.pages || 1} total={renewals.total || 0} label="renewals" onPageChange={(p) => { setRenewalsPage(p); loadAll(adminToken); }} />
          </TabsContent>

          {/* ─── Invoices Tab ─── */}
          <TabsContent value="invoices">
            <div className="mb-4 p-3 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20">
              <p className="text-sm font-semibold text-[#10B981]">Total Revenue: ${invoices.total_revenue} ({invoices.total} invoices)</p>
            </div>
            <div className="space-y-2" data-testid="admin-invoices-list">
              {invoices.invoices?.map((inv) => (
                <div key={inv.invoice_id} className="p-3 rounded-xl border border-border bg-card flex items-center justify-between">
                  <div>
                    <p className="text-sm text-foreground">{inv.billing_name || inv.billing_email || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">{inv.plan_name} · {new Date(inv.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-foreground">${inv.amount_usd}</p>
                    <p className="text-xs text-muted-foreground font-mono">#{inv.invoice_id}</p>
                  </div>
                </div>
              ))}
            </div>
            <Paginator page={invoicesPage} pages={invoices.pages || 1} total={invoices.total || 0} label="invoices" onPageChange={(p) => { setInvoicesPage(p); loadAll(adminToken); }} />
          </TabsContent>

          {/* ─── Widgets Tab ─── */}
          <TabsContent value="widgets">
            <div className="space-y-2" data-testid="admin-widgets-list">
              {(widgets.widgets || []).map((w) => (
                <div key={w.widget_id} className="p-3 rounded-xl border border-border bg-card flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{w.bot_name}</p>
                    <p className="text-xs text-muted-foreground">{w.owner_email} · ID: {w.widget_id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-foreground">{w.messages_this_month} msgs</p>
                    <p className="text-xs text-muted-foreground">{w.theme} theme</p>
                  </div>
                </div>
              ))}
            </div>
            <Paginator page={widgetsPage} pages={widgets.pages || 1} total={widgets.total || 0} label="widgets" onPageChange={(p) => { setWidgetsPage(p); loadAll(adminToken); }} />
          </TabsContent>
          <TabsContent value="blog">
            <div className="space-y-4" data-testid="admin-blog">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Blog Posts</h3>
                <Button size="sm" className="gap-1.5" onClick={() => { setEditingPost(null); setBlogForm({ title: '', slug: '', excerpt: '', content: '', cover_image: '', tags: '', published: false, meta_title: '', meta_description: '' }); setShowBlogForm(true); }} data-testid="new-blog-post-btn">
                  <Plus className="h-4 w-4" /> New Post
                </Button>
              </div>

              {/* Blog Form */}
              {showBlogForm && (
                <div className="p-5 rounded-xl border border-border bg-card space-y-3" data-testid="blog-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Input value={blogForm.title} onChange={(e) => { const t = e.target.value; setBlogForm(f => ({ ...f, title: t, slug: f.slug || t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })); }} placeholder="Post title" data-testid="blog-title-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Slug (URL)</Label>
                      <Input value={blogForm.slug} onChange={(e) => setBlogForm(f => ({ ...f, slug: e.target.value }))} placeholder="post-url-slug" data-testid="blog-slug-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Excerpt</Label>
                    <Input value={blogForm.excerpt} onChange={(e) => setBlogForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Short summary for the blog listing" data-testid="blog-excerpt-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Content (supports basic markdown: # headings, **bold**, - lists)</Label>
                    <textarea value={blogForm.content} onChange={(e) => setBlogForm(f => ({ ...f, content: e.target.value }))} placeholder="Write your blog post content here..." rows={12} className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-mono" data-testid="blog-content-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Cover Image URL</Label>
                      <Input value={blogForm.cover_image} onChange={(e) => setBlogForm(f => ({ ...f, cover_image: e.target.value }))} placeholder="https://example.com/image.jpg" data-testid="blog-cover-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Tags (comma-separated)</Label>
                      <Input value={blogForm.tags} onChange={(e) => setBlogForm(f => ({ ...f, tags: e.target.value }))} placeholder="ai, chatbot, support" data-testid="blog-tags-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">SEO Title (optional)</Label>
                      <Input value={blogForm.meta_title} onChange={(e) => setBlogForm(f => ({ ...f, meta_title: e.target.value }))} placeholder="Custom SEO title" data-testid="blog-meta-title-input" />
                    </div>
                    <div>
                      <Label className="text-xs">SEO Description (optional)</Label>
                      <Input value={blogForm.meta_description} onChange={(e) => setBlogForm(f => ({ ...f, meta_description: e.target.value }))} placeholder="Custom meta description" data-testid="blog-meta-desc-input" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                      <input type="checkbox" checked={blogForm.published} onChange={(e) => setBlogForm(f => ({ ...f, published: e.target.checked }))} className="rounded" data-testid="blog-published-check" />
                      Publish immediately
                    </label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setShowBlogForm(false)}>Cancel</Button>
                      <Button size="sm" onClick={async () => {
                        if (!blogForm.title || !blogForm.content) { toast.error('Title and content required'); return; }
                        const payload = { ...blogForm, tags: blogForm.tags ? blogForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
                        try {
                          const h = getAuthHeaders();
                          if (editingPost) {
                            await axios.put(`${API}/admin/blog/${editingPost}`, payload, h);
                            toast.success('Post updated');
                          } else {
                            await axios.post(`${API}/admin/blog`, payload, h);
                            toast.success('Post created');
                          }
                          setShowBlogForm(false);
                          loadAll(adminToken);
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
                      }} data-testid="blog-save-btn">
                        {editingPost ? 'Update Post' : 'Create Post'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Blog Posts List */}
              {(!blogPosts.posts || blogPosts.posts.length === 0) && !showBlogForm ? (
                <div className="text-center py-12 border border-dashed border-border rounded-xl">
                  <PenSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-muted-foreground">No blog posts yet. Create your first one!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(blogPosts.posts || []).map((post) => (
                    <div key={post.post_id} className="p-4 rounded-xl border border-border bg-card flex items-center justify-between" data-testid={`admin-blog-${post.post_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-foreground truncate">{post.title}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${post.published ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                            {post.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">/{post.slug} &middot; {new Date(post.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="View" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                          <Globe className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => {
                          setEditingPost(post.post_id);
                          setBlogForm({ title: post.title, slug: post.slug, excerpt: post.excerpt || '', content: post.content || '', cover_image: post.cover_image || '', tags: (post.tags || []).join(', '), published: post.published, meta_title: post.meta_title || '', meta_description: post.meta_description || '' });
                          setShowBlogForm(true);
                        }} data-testid={`edit-blog-${post.post_id}`}>
                          <PenSquare className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete" onClick={async () => {
                          if (!window.confirm(`Delete "${post.title}"?`)) return;
                          try { await axios.delete(`${API}/admin/blog/${post.post_id}`, getAuthHeaders()); toast.success('Post deleted'); loadAll(adminToken); } catch { toast.error('Failed to delete'); }
                        }} data-testid={`delete-blog-${post.post_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Paginator page={blogPage} pages={blogPosts.pages || 1} total={blogPosts.total || 0} label="posts" onPageChange={(p) => { setBlogPage(p); loadAll(adminToken); }} />
          </TabsContent>

          {/* ─── Demo Chats Tab ─── */}
          <TabsContent value="demo-chats">
            <div className="space-y-4" data-testid="admin-demo-chats">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Website Demo Conversations</h3>
                  <p className="text-xs text-muted-foreground">{demoChats.total} conversations from whisperai.one chat widget</p>
                </div>
              </div>

              {(!demoChats.chats || demoChats.chats.length === 0) ? (
                <div className="text-center py-12 border border-dashed border-border rounded-xl">
                  <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-sm text-muted-foreground">No demo conversations yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Messages from visitors on the website will appear here.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {demoChats.chats.map((chat) => {
                    const isExp = expandedChat === chat.session_id;
                    const msgCount = chat.messages?.length || 0;
                    const firstMsg = chat.messages?.find(m => m.role === 'user')?.content || 'No messages';
                    const lastTime = chat.updated_at ? new Date(chat.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

                    return (
                      <div key={chat.session_id} className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors" onClick={() => setExpandedChat(isExp ? null : chat.session_id)}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center shrink-0">
                              <MessageSquare className="h-4 w-4 text-[#10B981]" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-foreground truncate">{firstMsg}</p>
                              <p className="text-xs text-muted-foreground">{lastTime} &middot; {msgCount} messages</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => {
                              e.stopPropagation();
                              if (!window.confirm('Delete this conversation?')) return;
                              axios.delete(`${API}/admin/demo-chats/${chat.session_id}`, getAuthHeaders())
                                .then(() => { toast.success('Deleted'); loadAll(adminToken); })
                                .catch(() => toast.error('Failed'));
                            }}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                            {isExp ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                          </div>
                        </div>

                        {isExp && chat.messages && (
                          <div className="border-t border-border p-4 bg-muted/10 max-h-[400px] overflow-y-auto space-y-2">
                            {chat.messages.map((msg, i) => (
                              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[75%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                                  msg.role === 'user'
                                    ? 'bg-[#2563EB] text-white rounded-br-sm'
                                    : 'bg-card border border-border text-foreground rounded-bl-sm'
                                }`}>
                                  {msg.content}
                                  {msg.ts && <p className={`text-[9px] mt-1 ${msg.role === 'user' ? 'text-white/50' : 'text-muted-foreground'}`}>{new Date(msg.ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Paginator page={demoChatPage} pages={demoChats.pages || 1} total={demoChats.total || 0} label="chats" onPageChange={(p) => { setDemoChatPage(p); loadAll(adminToken); }} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
